<template>
  <pos-layout>
    <template #navbar-button>
      <div class="navbar-button-wrapper">
        <a
          href="#"
          class="navbar-print-all"
          :class="{ 'disabled': processedTableOrders.length === 0 || processedTableOrders.every(order => order.items.length === 0) }"
          @click="printAllChecks()"
        >
          <span class="icon-wrapper">
            <svg class="icon icon-icon-print-1">
              <use xlink:href="#icon-icon-print-1" />
            </svg>
          </span>
          <span class="navbar-print-all__text">
            {{ $t("orderscreen.print-all-checks") }}
          </span>
        </a>
      </div>
    </template>

    <b-container
      fluid
      class="h-100 px-0"
    >
      <div class="h-100 orders">
        <swiper
          ref="subordersSwiper"
          :options="swiperOption"
          class="subordersSwiper row no-gutters h-100"
        >
          <swiper-slide
            v-for="(order, orderIndex) in processedTableOrders"
            :id="'orderItems'+orderIndex"
            :data-id="orderIndex"
            :key="orderIndex"
            class="col-12 col-md-6 col-lg-4 h-100 p-0"
          >
            <!--
              DOCUMENTED SEAM (Step 1): this `.orders-sidebar` is intentionally
              NOT unified into the shared components/orders/OrderDetailSidebar.
              Unlike OrdersList / OnlineOrders (which preview a single selected
              order), TableOrders renders MANY orders inside a swiper, each with
              a fundamentally different sidebar: item rows are selectable for the
              move-item flow (toggle / activeItemIndex / activeOrderIndex), there
              is no view-details/payments toggle, no refund button, a "Pay"
              action, and a 3-button print/kitchen/transfer footer. Forcing it
              into OrderDetailSidebar would be a risky logic rewrite with no
              on-device QA, so it stays a documented seam (see REFACTOR_QA_CHECKLIST.md).
            -->
            <div class="orders-sidebar">
              <div class="orders-head">
                <div class="orders-top">
                  <div class="d-flex align-items-center">
                    <div class="orders-top-num">
                      <span>
                        {{ $t("orderscreen.order-no") }}
                      </span>
                      <strong>
                        # {{ getOrderDisplayId(order) }}
                      </strong>
                    </div>

                    <div class="orders-top-btn ml-auto d-flex align-items-center">
                      <b-button
                        :to="{ name: 'Order', params: { id: (order.id || order.local_id), tableID: tableID } }"
                        variant="dark"
                      >
                        {{ $t("amountandpaymentmethod.edit-order") }}
                      </b-button>
                      <b-button
                        variant="secondary"
                        @click="cancelOrder(order)"
                      >
                        {{ $t("orderscreen.cancel-order") }}
                      </b-button>
                    </div>
                  </div>
                </div>

                <div class="orders-add">
                  <a
                    href="#"
                    class="btn-link"
                    style="opacity: 0"
                  >
                    <svg class="icon icon-icon-add">
                      <use xlink:href="#icon-icon-add" />
                    </svg>

                    <span>
                      {{ $t("orderscreen.add-customer") }}
                    </span>
                  </a>

                  <a
                    v-if="activeOrderIndex !== null && activeOrderIndex !== orderIndex"
                    class="btn btn-warning btn-link-behav"
                    @click="moveItem(orderIndex)"
                  >
                    {{ $t("orderscreen.move") }}
                  </a>
                </div>
              </div>

              <div class="orders-sidebar-body">
                <div
                  class="orders-list"
                  role="tablist"
                >
                  <b-card
                    v-for="(item, index) in filterOutVoidItems(order.items)"
                    :key="index"
                    :class="{'selected': index == activeItemIndex && orderIndex == activeOrderIndex }"
                    no-body
                    @click="toggle({item, index},{order, orderIndex})"
                  >
                    <b-card-header
                      header-tag="header"
                      class="orders-item-head p-0"
                      role="tab"
                    >
                      <b-button
                        block
                        href="#"
                      >
                        <div class="d-flex">
                          <div class="btn-num">
                            {{ item.quantity }}
                          </div>

                          <div class="btn-body">
                            <div class="d-flex align-items-center justify-content-between top">
                              <div class="text-left">
                                {{ item.name }}
                              </div>
                              <div class="text-right">
                                {{ currency }} {{ (getItemGrandTotal(item)).toFixed(2) }}
                              </div>
                            </div>
                            <div class="d-flex align-items-center justify-content-between sub">
                              <div class="text-left">
                                {{ $t("orderscreen.discount") }}
                              </div>
                              <div class="text-right">
                                {{ currency }} {{ item.discount.toFixed(2) }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </b-button>
                    </b-card-header>
                    <div
                      v-if="item.notes && item.notes.length"
                      class="card-notes"
                    >
                      {{ item.notes }}
                    </div>
                  </b-card>
                </div>
              </div>

              <div class="orders-bottom">
                <div class="orders-bottom-item">
                  <span class="text-left">
                    {{ $t("ordertotal.total") }}
                  </span>
                  <span class="text-right">
                    {{ currency }} {{ order.grand_total ? order.grand_total.toFixed(2) : "0.00" }}
                  </span>
                </div>
                <div
                  v-if="isPaymentTypesAvailable"
                  class="orders-bottom-options"
                >
                  <div
                    :class="{'disabled': order.subtotal<=0 || !isPaymentTypesAvailable }"
                    class="pay"
                    @click="goToCheckout(order)"
                  >
                    <div class="d-flex align-items-center justify-content-between">
                      <span class="text-left">
                        {{ $t("orderscreen.pay") }}
                      </span>
                      <span class="text-right">
                        {{ currency }} {{ order.balance_remaining ? order.balance_remaining.toFixed(2) : "0.00" }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="print">
                  <div class="d-flex h-100 no-gutters">
                    <div class="h-100 w-100">
                      <b-button
                        :disabled="order.items.length === 0"
                        variant="link"
                        class="w-100"
                        @click="printCheck(order)"
                      >
                        <svg class="icon icon-icon-print-1">
                          <use xlink:href="#icon-icon-print-1" />
                        </svg>
                        <span>
                          {{ $t("amountandpaymentmethod.print-check") }}
                        </span>
                      </b-button>
                    </div>
                    <div class="h-100 w-100">
                      <b-button
                        :class="{ 'btn-disabled': !getKitchenUpdatesAvailableForOrder(order) }"
                        variant="link"
                        class="w-100"
                        @click="printKitchenCheckForOrder(order)"
                      >
                        <svg class="icon icon-icon-print-2">
                          <use xlink:href="#icon-icon-print-2" />
                        </svg>
                        <span>
                          {{ $t("amountandpaymentmethod.kitchen-print") }}
                        </span>
                      </b-button>
                    </div>
                    <div
                      v-if="terminalMode !== 'QUICK_SERVICE' || showTablesInQuickService"
                      class="h-100 w-100"
                    >
                      <b-button
                        :disabled="order.items.length === 0"
                        variant="link"
                        class="w-100"
                        @click="transferOrder(order)"
                      >
                        <svg class="icon icon-icon-move">
                          <use xlink:href="#icon-icon-move" />
                        </svg>
                        <span>
                          {{ $t("more-options-modal.move") }}
                        </span>
                      </b-button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </swiper-slide>
        </swiper>

        <div class="total">
          <div
            v-for="(order, orderIndex) in processedTableOrders"
            :id="'orderItems'+orderIndex"
            :data-id="orderIndex"
            :key="orderIndex"
            class="total-item"
            @click="navigateToOrder(orderIndex)"
          >
            <div class="total-head">
              <strong># {{ getOrderDisplayId(order) }}</strong>
            </div>

            <div class="total-body">
              <ul class="list-unstyled">
                <li>
                  {{ currency }} {{ order.subtotal.toFixed(2) }}
                </li>
                <li>
                  <strong class="text-danger">
                    {{ currency }} {{ order.grand_total.toFixed(2) }}
                  </strong>
                </li>
              </ul>
            </div>
          </div>

          <!--          <router-link tag="div" class="total-add" :to="{ name: 'Order', params: { tableID: tableID, id: 'new'  } }">-->
          <div
            class="total-add"
            @click="newOrderAtTable($route.params.id)"
          >
            <!--            <router-link tag="div" class="pay" :class="{'disabled': order.subtotal<=0 }" :to="{ name: 'OrderCheckout', params: { tableID: tableID, orderID: order.id  } }">-->
            <div class="total-add-head">
              {{ $t("orderscreen.add-order") }}
            </div>
            <div class="total-add-body">
              +
            </div>
          </div>
          <!--</router-link>-->
        </div>
      </div>
      <scale-loader
        :loading="isLoad"
        :color="'#007bff'"
      />
    </b-container>
  </pos-layout>
</template>

<script>
import PosLayout from "../layouts/PosLayout";
import orderMixin from "../mixins/order/orderMixin";
import { TAX_OPTION } from "@/helpers/store/tax_options";
import ScaleLoader from "vue-spinner/src/ScaleLoader";
import { mapGetters, mapActions } from "vuex";
import cdOrderMixin from "@/mixins/cdOrderMixin"
import { printReceipt } from "@/helpers/printHelper";
import liveObjectsService from "@/plugins/liveObjects";

export default {
  name: "TableOrders",
  components: {
    PosLayout,
    ScaleLoader
  },
  mixins: [orderMixin, cdOrderMixin],
  data () {
    return {
      isLoad: false,
      swiperOption: {
        slidesPerView: "auto",
        spaceBetween: 0,
        freeMode: true,
        touchRatio: 1,
        touchAngle: 90,
        shortSwipes: false,
        threshold: 30,
        slideToClickedSlide: false,
        pagination: {
          el: ".swiper-pagination",
          clickable: true
        },
        breakpoints: {
          992: {
            slidesPerView: 2,
            spaceBetween: 0
            // simulateTouch: false
          },
          768: {
            slidesPerView: 1,
            spaceBetween: 0
            // simulateTouch: false
          }
        }
      },
      tax: "$",
      tableOrders: [],
      selectedItem: {
        id: 35279,
        orderID: null
      },
      activeItemIndex: null,
      activeOrderIndex: null,
      tableID: 0,
      table: null,
      is_tax_inclusive: false,
      // Order ids this terminal currently holds a presence lock on, so we
      // only enter/leave for what actually changed rather than re-entering
      // on every store mutation (allOrders is deep-watched).
      presenceOrderIds: []
    };
  },
  computed: {
    ...mapGetters({
      terminal: "config/terminal",
      store: "config/store",
      isKioskMode: "config/isKioskMode",
      currency: "config/currency",
      employee: "employee",
      allOrders: 'orders/getAllOrders',
      getTableById: "config/getTableById",
      terminalMode: "terminalMode",
      products: "config/products"
    }),
    showTablesInQuickService() {
      // Check if show_tables db_setting is true
      if (this.terminal && this.terminal.db_settings) {
        const showTablesSetting = this.terminal.db_settings.find(setting => setting.name === 'show_tables');
        return showTablesSetting ? showTablesSetting.value === 'true' : false;
      }
      return false;
    },
    // Get the table name for this view
    currentTableName() {
      if (this.table && this.table.table_name) {
        return this.table.table_name;
      }
      return null;
    },
    // Processed table orders with printer assignments
    processedTableOrders() {
      if (!this.tableOrders || this.tableOrders.length === 0) {
        return [];
      }
      
      return this.tableOrders.map(order => {
        if (order.items && order.items.length > 0) {
          const processedItems = order.items.map(item => {
            const productConfig = this.products.find(product => product.id === item.product);
            // Only update printers if product config exists and has printers
            if (productConfig && productConfig.printers && productConfig.printers.length > 0) {
              return { ...item, printers: productConfig.printers };
            }
            // Preserve existing has_kitchen_updates and sent_to_kitchen flags
            return item;
          });
          return { ...order, items: processedItems };
        }
        return order;
      });
    }
  },
  watch: {
    '$route.params.id': {
      handler: function(newId) {
        if (newId) {
          this.init();
        }
      },
      immediate: false
    },
    allOrders: {
      handler: function() {
        this.init();
      },
      deep: true
    }
  },
  async created() {
    this.isLoad = true;
    try {
      await this.fetchTaxFromDb()
      await this.fetchTablesFromDb()
      await this.fetchOrdersFromDb()
      await this.fetchProductsFromDb()
      this.tax = this.$store.getters["config/tax"].rate;
      this.is_tax_inclusive = this.$store.getters["config/store"].tax_option == TAX_OPTION.TAX_INCLUSIVE;
      this.tableID = parseInt(this.$route.params.id);
      this.init();
    } catch (e) {
      console.error("Error loading table orders", e);
    } finally {
      this.isLoad = false;
    }
    window.addEventListener("beforeunload", this.releaseAllPresence);
    window.addEventListener("pagehide", this.releaseAllPresence);
  },
  beforeDestroy() {
    this.releaseAllPresence();
    window.removeEventListener("beforeunload", this.releaseAllPresence);
    window.removeEventListener("pagehide", this.releaseAllPresence);
  },
  methods: {
    ...mapActions({
      fetchTaxFromDb: 'config/fetchTaxFromDb',
      fetchTablesFromDb: 'config/fetchTablesFromDb',
      fetchOrdersFromDb:'orders/fetchOrdersFromDb',
      fetchProductsFromDb: 'config/fetchProductsFromDb'
    }),
    init: function () {
      this.table = this.$store.getters.getTableWithOrders(parseInt(this.$route.params.id));
      if (this.table) {
        this.tableOrders = this.table.tableOrders;
      } else {
        this.tableOrders = [];
        console.error(`Table with id ${this.$route.params.id} not found`);
      }
      this.syncPresence();
    },
    // Holds the presence lock for every split order currently shown on this
    // table view, for as long as this view is open — items can be moved
    // between checks, cancelled, etc. entirely in place here with no route
    // change, so entry-time locking alone isn't enough.
    syncPresence() {
      if (!liveObjectsService.isReady()) {
        return;
      }
      const currentIds = this.tableOrders
        .map(order => order.id || order.local_id)
        .filter(Boolean)
        .map(String);

      const toEnter = currentIds.filter(id => !this.presenceOrderIds.includes(id));
      const toLeave = this.presenceOrderIds.filter(id => !currentIds.includes(id));

      toEnter.forEach(id => liveObjectsService.enterOrderPresence(id));
      toLeave.forEach(id => liveObjectsService.leaveOrderPresence(id));

      this.presenceOrderIds = currentIds;
    },
    releaseAllPresence() {
      this.presenceOrderIds.forEach(id => liveObjectsService.leaveOrderPresence(id));
      this.presenceOrderIds = [];
    },
    goToCheckout: async function (order) {
      if (!this.isPaymentTypesAvailable) return;
      this.sendOrderToChannel(order)
      await this.$router.push({
        name: "OrderCheckout",
        params: { id: (order.id || order.local_id) }
      });
    },
    getOrderKey(order) {
      return order ? (order.id || order.local_id) : null;
    },
    toggle: function (itemData, orderData) {
      if (this.activeItemIndex === itemData.index && this.activeOrderIndex === orderData.orderIndex)
        return this.unselectItem();
      this.activeOrderIndex = orderData.orderIndex;
      this.activeItemIndex = itemData.index;
      this.selectedItem = {
        itemID: itemData.item.id,
        itemData,
        orderData
      };
    },
    unselectItem () {
      this.activeOrderIndex = null;
      this.activeItemIndex = null;
    },
    recalculateSums: function (orderIndex) {
      // todo move to mixing or external helper, or store
      const self = this;
      this.tableOrders[orderIndex].subtotal = 0;
      this.tableOrders[orderIndex].tax_total = 0;
      this.tableOrders[orderIndex].items.filter(item => item.void_status !== 1).forEach(function (item) {
        let itemModsSum = 0;
        if (item.modifiers) {
          for (const modId in item.modifiers) {
            if (!item.modifiers[modId].void_status) {
              itemModsSum += item.modifiers[modId].price;
            }
          }
        }
        self.tableOrders[orderIndex].subtotal = Math.max(0,
          self.tableOrders[orderIndex].subtotal + (item.quantity * (item.price_base + itemModsSum - item.discount)));
        self.tableOrders[orderIndex].discount_total = parseFloat((self.tableOrders[orderIndex].discount_total + (item.discount * item.quantity)).toFixed(2));
        if (item.tax) {
          self.tableOrders[orderIndex].tax_total += item.tax * item.quantity;
        }
      });
      this.tableOrders[orderIndex].grand_total = parseFloat((this.tableOrders[orderIndex].subtotal
        + this.tableOrders[orderIndex].tax_total).toFixed(2));

      const order = self.tableOrders[orderIndex];
      let paymentEntriesSum = 0;
      order.payments && order.payments.map(payment => paymentEntriesSum += payment.amount);
      this.tableOrders[orderIndex].balance_remaining = order.subtotal + (this.is_tax_inclusive ? 0
        : order.tax_total - paymentEntriesSum);
    },
    moveItem: async function (receiverOrderIndex) {
      const senderOrderIndex = this.selectedItem.orderData.orderIndex;
      this.activeItemIndex = this.tableOrders[receiverOrderIndex].items.length;

      const movingItem = this.selectedItem.itemData.item;

      // itemData.index is an index into the filterOutVoidItems() filtered array, NOT the raw
      // items array. When voided items exist at lower positions the indices diverge, so we must
      // locate the correct raw slot by matching local_id (falling back to id).
      const senderItems = this.tableOrders[senderOrderIndex].items;
      const movingKey = movingItem.local_id || movingItem.id;
      const rawIndex = senderItems.findIndex(item =>
        (item.local_id || item.id) === movingKey && item.void_status !== 1
      );

      if (rawIndex === -1) return; // safety: item already gone

      // Void the item on the sender so the backend retires it (the safeguard never deletes
      // items absent from a payload — it only acts on items explicitly included).
      this.$set(senderItems, rawIndex, { ...movingItem, void_status: 1 });

      // Push a fresh copy onto the receiver — no remote id so the backend creates a new row.
      this.tableOrders[receiverOrderIndex].items.push({
        ...movingItem,
        id: null,
        local_id: `move_${Date.now()}_${Math.random().toString(36).slice(2)}`
      });

      // Recalculate
      this.recalculateSums(receiverOrderIndex);
      this.recalculateSums(senderOrderIndex);
      this.activeOrderIndex = receiverOrderIndex;
      this.selectedItem.orderData.orderIndex = receiverOrderIndex;
      this.selectedItem.itemData.index = this.activeItemIndex;
      const receiver = this.tableOrders[receiverOrderIndex];
      const sender = this.tableOrders[senderOrderIndex];
      this.$store.dispatch("orders/saveOrder", receiver);
      this.$store.dispatch("orders/saveOrder", sender);
      this.$store.dispatch("orders/enqueueOrderSync", { order: receiver, trigger: "table_move_item" })
        .then(() => this.$store.dispatch("orders/enqueueOrderSync", { order: sender, trigger: "table_move_item" }))
        .then(() => this.$store.dispatch("orders/drainOrderSyncOutbox"))
        .catch(() => {});
      this.unselectItem();
    },
    updateLocalId: async function (msgObj) {
      const index = this.tableOrders.findIndex(order => this.getOrderKey(order) === msgObj.localID);
      if (index !== -1) {
        this.tableOrders[index].id = msgObj.remoteID;
      }
    },
    newOrderAtTable: async function (tableID) {
      // Ensure tableID is an integer
      const parsedTableID = parseInt(tableID);
      
      // Validate that we have a valid table ID
      if (isNaN(parsedTableID) || parsedTableID === 0) {
        console.error('Invalid table ID:', tableID);
        return;
      }
      
      let diningOptionType = this.store.default_dining_option;
      let diningOptionName = null;

      // Override dining option to "Dine-in" when terminal is in TABLE_SERVICE mode
      if (this.terminalMode === "TABLE_SERVICE" && this.terminal["dining_options"]) {
        const dineInOption = this.terminal["dining_options"].find(option => 
          option.name && option.name.toLowerCase().includes("dine") && option.name.toLowerCase().includes("in")
        );
        if (dineInOption) {
          diningOptionType = dineInOption.id;
          diningOptionName = dineInOption.name;
        }
      }

      const newOrder = await this.$store.dispatch("orders/initNewOrder", {
        table: parsedTableID,
        related_terminal: this.terminal ? this.terminal.id : null,
        dining_option_type: diningOptionType,
        dining_option: diningOptionName,
        cash_discount:false
      });

      await this.$store.dispatch("orders/createRemoteOrder", newOrder.local_id);

      const table = this.$store.getters.getTableWithOrders(parsedTableID);
      if (table) {
        this.tableOrders = table.tableOrders;
      } else {
        console.error(`Table with id ${parsedTableID} not found after creating order`);
      }
    },
    navigateToOrder(index) {
      const swiper = this.$refs.subordersSwiper;
      if (swiper && swiper.$swiper) {
        const slideWidth = swiper.$el.querySelector('.swiper-slide').offsetWidth;
        const offset = window.innerWidth >= 992 ? slideWidth / 2 : 0;
        swiper.$swiper.translateTo(-(index * slideWidth - offset), 300);
      }
    },
    filterOutVoidItems: function (items) {
      return items.filter(function (item) {
        return item.void_status !== 1;
      });
    },
    printCheck: async function (order) {
      if (!order || order.items.length === 0) {
        return;
      }
      
      const printPdf = this.terminal.db_settings.find((item) => item.name === "printer_pdf")
        && this.terminal.db_settings.find((item) => item.name === "printer_pdf")["value"] === "true";
      
      if (printPdf) {
        window.print();
      } else {
        try {
          await printReceipt(order);
        } catch (e) {
          this.$store.dispatch("errorModal/displayModalError", {
            modalId: "print-error",
            errorBody: {
              title: "Printer error",
              info: "Please, check that you have a correct IP for printer and Qz tray is running"
            }
          });
          console.error("could not print receipt", e);
        }
      }
    },
    printKitchenCheckForOrder: async function (order) {
      if (!order || order.items.length === 0) {
        return;
      }
      
      try {
        // Get kitchen items for this order
        const kitchenItems = order.items.filter(item => item.void_status !== 1 && item.quantity > 0);
        if (kitchenItems.length === 0) {
          return;
        }
        
        // Create a temporary current order context for kitchen print
        const originalCurrentOrder = this.currentOrder;
        this.currentOrder = order;
        
        // Call kitchen print from mixin
        await this.printKitchenCheck();
        
        // Restore original current order
        this.currentOrder = originalCurrentOrder;
      } catch (e) {
        this.$store.dispatch("errorModal/displayModalError", {
          modalId: "print-error",
          errorBody: {
            title: "Kitchen printer error",
            info: "Please, check that you have a correct IP for printer and Qz tray is running"
          }
        });
        console.error("could not print kitchen receipt", e);
      }
    },
    // cancelOrderModalPromise is provided by orderMixin (shared confirm dialog);
    // the previous local copy here was an exact duplicate (Phase 5 dedupe).
    cancelOrder: async function (order) {
      if (!order) {
        return;
      }
      
      const self = this;
      // Void-reason modal doubles as the confirmation when reasons are configured
      // (see confirmOrderCancel in orderMixin); never chain confirm + prompt.
      const { confirmed, voidReason } = await this.confirmOrderCancel();
      if (!confirmed) return;

      // Temporarily set currentOrder so the cancelOrderProcessing can work with it
      const originalCurrentOrder = self.currentOrder;
      self.currentOrder = order;

      try {
        await self.cancelOrderProcessing({ voidReason });

        // Reload table orders
        self.init();
      } catch (e) {
        console.error("Error cancelling order", e);
      } finally {
        // Restore original current order
        self.currentOrder = originalCurrentOrder;
      }
    },
    transferOrder: async function (order) {
      if (!order || order.items.length === 0) {
        return;
      }
      
      // Get the order's current table ID (from the current route)
      const currentTableID = this.$route.params.id;
      
      // Navigate to tables page with transfer order params (exactly like MoreOptionsModal)
      this.$router.push({
        name: 'TablesMoveOrder',
        params: { 
          tableID: currentTableID, 
          moveorder: order.id || order.local_id
        }
      });
    },
    printAllChecks: async function () {
      if (this.processedTableOrders.length === 0) {
        return;
      }
      
      const ordersToPrint = this.processedTableOrders.filter(order => order.items.length > 0);
      
      for (const order of ordersToPrint) {
        try {
          await this.printCheck(order);
          // Add a small delay between prints to avoid printer overload
          await new Promise(resolve => setTimeout(resolve, 500));
        } catch (e) {
          console.error("could not print receipt for order", order.id, e);
        }
      }
    },
    // Get formatted order display ID with table name
    getOrderDisplayId(order) {
      const orderId = order.id || order.local_id;
      
      // If we have a table name, add it as a prefix
      if (this.currentTableName) {
        return `${this.currentTableName} / ${orderId}`;
      }
      
      return orderId;
    },
    // Check if kitchen updates are available for a specific order (same logic as printMixin)
    getKitchenUpdatesAvailableForOrder(order) {
      if (!order || !order.items || order.items.length === 0) {
        return false;
      }
      
      // Check for new kitchen items that haven't been sent to kitchen
      const isNewKitchenItemNotPrinted = order.items.filter((e) => 
        (e.void_status === 0 || e.void_status === null) && 
        !e.sent_to_kitchen && 
        e.printers && 
        e.printers.length > 0
      ).length > 0;
      
      // Check for items with kitchen updates
      const isKitchenUpdates = order.items.filter((e) => 
        (e.void_status === 0 || e.void_status === null) && 
        e.has_kitchen_updates && 
        e.printers && 
        e.printers.length > 0
      ).length > 0;
      
      return isKitchenUpdates || isNewKitchenItemNotPrinted;
    }
  }
};
</script>

<style lang="scss" scoped>
.swiper-container {
  // overflow: visible;
  padding-right: 120px;

  .orders-sidebar:after {
    content: none;
  }

  .swiper-slide {
    margin-right: 2px;
  }
}

.swiper-pagination {
  display: none;
}

.navbar-button-wrapper {
  flex: 0 0 auto;
  margin-left: 0;
  margin-right: 0;
}

.navbar-print-all {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  gap: 8px;
  height: 36px;
  min-height: 36px;
  max-height: 36px;
  min-width: 36px;
  padding: 0 12px;
  color: #fff;
  text-decoration: none;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  font-size: 0.875rem;
  line-height: 1;
  white-space: nowrap;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    text-decoration: none;
    color: #fff;
  }
  
  &.disabled {
    opacity: 0.5;
    pointer-events: none;
    cursor: not-allowed;
  }
  
  .icon-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    
    svg.icon {
      width: 20px;
      height: 20px;
      filter: brightness(0) invert(1);
    }
  }

  @media (min-width: 768px) and (max-width: 1311.98px) {
    width: 36px;
    padding: 0;
    gap: 0;

    &__text {
      display: none;
    }
  }
}

// Add spacing between header buttons
.orders-top-btn {
  gap: 8px;
}

.orders-top-btn .btn + .btn {
  margin-left: 0;
}

</style>